import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    window.addEventListener('resize', this.#handleResize);

    this.updateVisibleSlides();
    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.suspend();
    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    window.removeEventListener('resize', this.#handleResize);
  }

  next() {
    this.current += this.slidesPerView;
  }

  previous() {
    this.current -= this.slidesPerView;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;
    this.updateVisibleSlides();
  }

  get slidesPerView() {
    let attribute;

    if (window.matchMedia('(max-width: 640px)').matches) {
      attribute = this.getAttribute('slides-per-view-mobile');
    } else if (window.matchMedia('(max-width: 1024px)').matches) {
      attribute = this.getAttribute('slides-per-view-tablet');
    } else {
      attribute = this.getAttribute('slides-per-view-desktop');
    }

    const value = parseInt(`${attribute}`, 10);
    const slideCount = (this.refs.slides ?? []).length;

    if (Number.isNaN(value)) return 1;

    return Math.min(Math.max(value, 1), Math.max(slideCount, 1));
  }

  updateVisibleSlides() {
    const slides = this.refs.slides ?? [];
    const slideCount = slides.length;
    const slidesPerView = this.slidesPerView;
    const allSlidesVisible = slideCount <= slidesPerView;
    const arrows = this.querySelector('slideshow-arrows');

    this.toggleAttribute('all-slides-visible', allSlidesVisible);

    if (arrows instanceof HTMLElement) {
      arrows.hidden = allSlidesVisible;
    }

    if (!slideCount) return;

    let relativeIndex = this.current % slideCount;
    if (relativeIndex < 0) {
      relativeIndex += slideCount;
    }

    slides.forEach((slide, index) => {
      const distance = (index - relativeIndex + slideCount) % slideCount;

      slide.setAttribute('aria-hidden', `${distance >= slidesPerView}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  #handleResize = () => this.updateVisibleSlides();
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
